import React, { useEffect, useState } from "react";
import { Pencil, Trash2, Plus, Clock } from "lucide-react";
import Sidebar from "../../../component/includes/sidebar";
import { 
  CreateFooterContent4Data, 
  FooterContent4, 
  footercontent4Service, 
  UpdateFooterContent4Data 
} from "../../../services/footer/timeline";

const FooterContent4Management: React.FC = () => {
  const [contacts, setContacts] = useState<FooterContent4[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<FooterContent4 | null>(null);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await footercontent4Service.getFooterContent4();
      setContacts(Array.isArray(response) ? response : []);
    } catch (err: any) {
      setError(err?.error || err?.message || "Failed to fetch contacts");
      setContacts([]);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const showError = (message: string) => {
    setError(message);
    setTimeout(() => setError(null), 3000);
  };

  const resetForm = () => {
    setEditingContact(null);
    setIsModalOpen(false);
    setIsCreateMode(false);
  };

  // Create
  const handleCreateContact = async () => {
    if (!editingContact) return;

    if (!editingContact.title.trim()) {
      showError("Title cannot be empty");
      return;
    }

    try {
      const createData: CreateFooterContent4Data = {
        title: editingContact.title,
        day_1: editingContact.day_1,
        time_start_1: editingContact.time_start_1,
        time_end_1: editingContact.time_end_1,
        day_2: editingContact.day_2,
        time_start_2: editingContact.time_start_2,
        time_end_2: editingContact.time_end_2,
        day_3: editingContact.day_3,
        time_start_3: editingContact.time_start_3,
        time_end_3: editingContact.time_end_3,
      };

      await footercontent4Service.createFooterContent4(createData);
      await fetchContacts();
      showSuccess("Contact created successfully");
      resetForm();
    } catch (err) {
      showError("Failed to create contact");
    }
  };

  // Update
  const handleUpdateContact = async () => {
    if (!editingContact || !editingContact.id) return;

    if (!editingContact.title.trim()) {
      showError("Title cannot be empty");
      return;
    }

    try {
      const updateData: UpdateFooterContent4Data = {
        id: editingContact.id,
        title: editingContact.title,
        day_1: editingContact.day_1,
        time_start_1: editingContact.time_start_1,
        time_end_1: editingContact.time_end_1,
        day_2: editingContact.day_2,
        time_start_2: editingContact.time_start_2,
        time_end_2: editingContact.time_end_2,
        day_3: editingContact.day_3,
        time_start_3: editingContact.time_start_3,
        time_end_3: editingContact.time_end_3,
      };

      await footercontent4Service.updateFooterContent4(updateData);
      await fetchContacts();
      showSuccess("Contact updated successfully");
      resetForm();
    } catch (err) {
      showError("Failed to update contact");
    }
  };

  // Delete
  const handleDeleteContact = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this contact?")) return;

    try {
      await footercontent4Service.deleteFooterContent4(id);
      await fetchContacts();
      showSuccess("Contact deleted successfully");
    } catch (err) {
      showError("Failed to delete contact");
    }
  };

  const filteredContacts = contacts.filter((contact) =>
    contact.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar />

      <div className="flex-1 flex flex-col">
<div className="flex-1 p-4 md:p-6 mt-14">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="bg-white rounded-3xl shadow-sm p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center">
                  <Clock size={32} className="text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-semibold text-gray-900">Footer Content 4 Management</h1>
                  <p className="text-gray-500 text-sm mt-1">Manage opening hours / timeline</p>
                </div>
              </div>

              <button
                onClick={() => {
                  setIsCreateMode(true);
                  setEditingContact({
                    id: undefined,
                    title: "",
                    day_1: "", time_start_1: "", time_end_1: "",
                    day_2: "", time_start_2: "", time_end_2: "",
                    day_3: "", time_start_3: "", time_end_3: "",
                    created_at: "",
                    updated_at: "",
                  });
                  setIsModalOpen(true);
                }}
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-2xl font-medium transition-all active:scale-95"
              >
                <Plus className="w-5 h-5" />
                Add New Contact
              </button>
            </div>

            {/* Search Bar */}
            <div className="mb-8">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by title..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-2xl py-4 pl-12 pr-6 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all"
                />
                <div className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400">🔍</div>
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-8 py-5 text-left font-medium text-gray-600 w-20">ID</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600">TITLE</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600 hidden lg:table-cell">SCHEDULE</th>
                    <th className="px-8 py-5 text-right font-medium text-gray-600 w-36">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredContacts.map((contact, index) => (
                    <tr key={contact.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-8 py-6">
                        <div className="w-9 h-9 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold text-lg">
                          {index + 1}
                        </div>
                      </td>
                      <td className="px-8 py-6 font-medium text-gray-800">
                        {contact.title}
                      </td>
                      <td className="px-8 py-6 text-gray-600 hidden lg:table-cell text-sm">
                        <div>
                          {contact.day_1 && <div>{contact.day_1}: {contact.time_start_1} - {contact.time_end_1}</div>}
                          {contact.day_2 && <div>{contact.day_2}: {contact.time_start_2} - {contact.time_end_2}</div>}
                          {contact.day_3 && <div>{contact.day_3}: {contact.time_start_3} - {contact.time_end_3}</div>}
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex items-center justify-end gap-3">
                          <button
                            onClick={() => {
                              setEditingContact(contact);
                              setIsCreateMode(false);
                              setIsModalOpen(true);
                            }}
                            className="p-3 text-blue-500 hover:bg-blue-50 rounded-2xl transition-all hover:scale-110"
                            title="Edit"
                          >
                            <Pencil className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteContact(contact.id!)}
                            className="p-3 text-red-500 hover:bg-red-50 rounded-2xl transition-all hover:scale-110"
                            title="Delete"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {filteredContacts.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-20 text-center text-gray-400">
                        No contacts found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Create / Edit */}
      {isModalOpen && editingContact && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-semibold mb-6">
              {isCreateMode ? "Create New Contact" : "Edit Contact"}
            </h2>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                <input
                  type="text"
                  value={editingContact.title}
                  onChange={(e) => setEditingContact({ ...editingContact, title: e.target.value })}
                  className="w-full border border-gray-300 rounded-2xl px-6 py-4 focus:outline-none focus:border-indigo-500"
                  placeholder="Contoh: Jam Buka"
                />
              </div>

              {/* Schedule 1 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Day 1</label>
                  <select
                    value={editingContact.day_1 || ""}
                    onChange={(e) => setEditingContact({ ...editingContact, day_1: e.target.value })}
                    className="w-full border border-gray-300 rounded-2xl px-4 py-3 focus:outline-none focus:border-indigo-500"
                  >
                    <option value="">Pilih Hari</option>
                    <option value="Senin">Senin</option>
                    <option value="Selasa">Selasa</option>
                    <option value="Rabu">Rabu</option>
                    <option value="Kamis">Kamis</option>
                    <option value="Jumat">Jumat</option>
                    <option value="Sabtu">Sabtu</option>
                    <option value="Minggu">Minggu</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                  <input
                    type="time"
                    value={editingContact.time_start_1 || ""}
                    onChange={(e) => setEditingContact({ ...editingContact, time_start_1: e.target.value })}
                    className="w-full border border-gray-300 rounded-2xl px-4 py-3 focus:outline-none focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                  <input
                    type="time"
                    value={editingContact.time_end_1 || ""}
                    onChange={(e) => setEditingContact({ ...editingContact, time_end_1: e.target.value })}
                    className="w-full border border-gray-300 rounded-2xl px-4 py-3 focus:outline-none focus:border-indigo-500"
                  />
                </div>
              </div>

              {/* Schedule 2 & 3 (sama seperti di atas, bisa di-copy jika perlu) */}
              {/* Untuk ringkasan, saya hanya tampilkan satu set. Kamu bisa tambahkan 2 set lagi dengan pola yang sama */}

              <div className="flex gap-4 mt-10">
                <button
                  onClick={resetForm}
                  className="flex-1 py-4 border border-gray-300 rounded-2xl font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={isCreateMode ? handleCreateContact : handleUpdateContact}
                  className="flex-1 py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-medium transition-all"
                >
                  {isCreateMode ? "Create Contact" : "Update Contact"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FooterContent4Management;